import { useContext, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark, faHourglassHalf } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import { currentUsername, scheduleIsForMe } from "@serie3/mymediaplanner/Modules/Social/Domain/delegation";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import ToastMessages from "../../../../ToastMessages";
import NewModal from "../../../shared/Modals/NewModal";
import GroupLogo from "../../../shared/GroupLogo";
import PostPreviewPanel from "../Previews/PostPreviewPanel";
import { ApprovalBadge, personName } from "./approvalStyles";
import WithdrawApprovalButton, { canWithdraw } from "./WithdrawApprovalButton";

/** Approve / reject in a modal footer, next to the close button. */
export const DecideButtons = ({ schedule, onDecided }) => {
  const { t } = useTranslation();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    setBusy(true);
    const r = await crudApi.edit(SocialEndpoints.posts.schedule.approvals.approve + schedule.id, {}, true);
    setBusy(false);
    if (r.ok) {
      ToastMessages.SuccessfulSave(t("Backend.Approvals.Approved", "Approved. It will be published on your account at the planned time."));
      onDecided(r.data);
    } else {
      ToastMessages.Error();
    }
  };

  const reject = async () => {
    setBusy(true);
    const r = await crudApi.edit(SocialEndpoints.posts.schedule.approvals.reject + schedule.id, { comment }, true);
    setBusy(false);
    if (r.ok) {
      ToastMessages.SuccessfulSave(t("Backend.Approvals.Rejected", "Rejected. The author has been told."));
      setRejecting(false);
      onDecided(r.data);
    } else {
      ToastMessages.Error();
    }
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginRight: "auto" }}>
      {rejecting && (
        <input
          id={`reject-comment-${schedule.id}`}
          type="text"
          className="form-control form-control-sm"
          style={{ minWidth: 260 }}
          placeholder={t("Backend.Approvals.RejectPlaceholder", "What should change? (optional)")}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); reject(); } }}
        />
      )}
      {!rejecting && (
        <Button onClick={approve} disabled={busy} style={{ background: "#0f7b86", border: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FontAwesomeIcon icon={faCheck} /> {t("Backend.Approvals.Approve", "Approve")}
        </Button>
      )}
      {rejecting ? (
        <>
          <Button variant="danger" onClick={reject} disabled={busy}>
            <FontAwesomeIcon icon={faXmark} /> {t("Backend.Approvals.ConfirmReject", "Send rejection")}
          </Button>
          <Button variant="secondary" onClick={() => setRejecting(false)} disabled={busy}>
            {t("Backend.Buttons.Cancel", "Cancel")}
          </Button>
        </>
      ) : (
        <Button variant="outline-danger" onClick={() => setRejecting(true)} disabled={busy}>
          {t("Backend.Approvals.Reject", "Reject")}
        </Button>
      )}
    </div>
  );
};

const groupInitials = (name) => {
  const words = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words.length > 1 ? words.slice(0, 2).map((w) => w[0].toUpperCase()).join("") : words[0].slice(0, 2).toUpperCase();
};

/**
 * The approvals table plus its post modal, shared by the group tab and the
 * sidebar inbox. Rows that wait for the viewer's decision are highlighted and
 * sorted first; the modal shows the two-column preview and, for the
 * publishing user, Approve / Reject or Remove approval in the footer.
 *
 * `loadFiles(schedule)` -> Promise<{ files, binaryEndpoint? }> supplies the
 * attached images; each context knows which route it may use.
 */
const ApprovalScheduleList = ({ schedules, onDecided, loadFiles, showGroup = false, showTitle = true, showFilter = true, emptyText = null }) => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const myUsername = currentUsername(settings);
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewBinary, setPreviewBinary] = useState(undefined);

  const needsMe = (s) => s.approval_status === "proposed" && scheduleIsForMe(s, myUsername);
  const filtered = (schedules ?? []).filter((s) => {
    if (!filter) return true;
    if (filter === "mine") return needsMe(s);
    return s.approval_status === filter;
  });
  const rows = [...filtered].sort((a, b) => Number(needsMe(b)) - Number(needsMe(a)));
  const pendingCount = (schedules ?? []).filter((s) => s.approval_status === "proposed").length;
  const mineCount = (schedules ?? []).filter(needsMe).length;

  const openPreview = async (schedule) => {
    setPreviewFiles([]);
    setPreviewBinary(undefined);
    setPreview(schedule);
    if (!loadFiles) return;
    try {
      const r = await loadFiles(schedule);
      if (r) {
        setPreviewFiles(Array.isArray(r.files) ? r.files : []);
        setPreviewBinary(r.binaryEndpoint);
      }
    } catch (e) {
      /* no images for this viewer */
    }
  };

  const decided = (updated) => {
    if (!updated?.id) return;
    onDecided?.(updated);
    setPreview((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const groupsOf = (s) => (Array.isArray(s?.post?.shared_in) ? s.post.shared_in : []);

  return (
    <>
      <NewModal
        show={Boolean(preview)}
        setShow={(show) => { if (!show) setPreview(null); }}
        title={preview?.post?.title || t("Backend.Approvals.Untitled", "Untitled post")}
        size="xl"
        noPadding
        ExtraButton={
          preview && needsMe(preview)
            ? () => <DecideButtons key={preview.id} schedule={preview} onDecided={decided} />
            : preview && canWithdraw(preview, myUsername)
            ? () => <WithdrawApprovalButton key={preview.id} schedule={preview} onDone={decided} style={{ marginRight: "auto" }} />
            : null
        }
      >
        {preview && (
          <PostPreviewPanel
            post={preview.post}
            author={preview.planned_by}
            schedules={[preview]}
            files={previewFiles}
            binaryEndpoint={previewBinary}
          >
            {preview.approval_comment && preview.approval_status !== "approved" && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "#9299a8", marginBottom: 6 }}>
                  {t("Backend.Approvals.Comment", "Comment")}
                </div>
                <div style={{ fontSize: 13, color: "#74788d", borderLeft: "3px solid #d5dae2", paddingLeft: 10 }}>{preview.approval_comment}</div>
              </div>
            )}
          </PostPreviewPanel>
        )}
      </NewModal>

      {(showTitle || showFilter) && (
      <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
        {showTitle ? (
          <h5 style={{ fontWeight: 700, color: "#1a2236", margin: 0 }}>
            {t("Backend.Groups.Approvals.Title", "Planned on members' accounts")}
            {pendingCount > 0 && <span style={{ color: "#8a5a00", marginLeft: 8, fontSize: 13 }}>{pendingCount} {t("Backend.Groups.Approvals.Pending", "waiting")}</span>}
          </h5>
        ) : <span />}
        {showFilter && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#495057" }}>
          <span style={{ fontWeight: 600, color: "#6b7280", fontSize: 12 }}>{t("Backend.Groups.Approvals.Filter", "Show:")}</span>
          <select
            id="approvals-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", minWidth: 200, background: "#fff", color: "#1a2236" }}
          >
            <option value="">{t("Backend.Groups.Approvals.FilterAll", "All")}</option>
            <option value="mine">{t("Backend.Groups.Approvals.FilterMine", "Waiting for my approval")}{mineCount > 0 ? ` (${mineCount})` : ""}</option>
            <option value="proposed">{t("Backend.Groups.Approvals.FilterProposed", "Waiting for approval")}</option>
            <option value="approved">{t("Backend.Groups.Approvals.FilterApproved", "Approved")}</option>
            <option value="rejected">{t("Backend.Groups.Approvals.FilterRejected", "Rejected")}</option>
            <option value="cancelled">{t("Backend.Groups.Approvals.FilterCancelled", "Cancelled")}</option>
          </select>
        </label>
        )}
      </div>
      )}

      {rows.length === 0 ? (
        <div style={{ border: "2px dashed #e0e4ea", borderRadius: 12, padding: "28px 24px", textAlign: "center", color: "#adb5bd", fontSize: 13 }}>
          {filter
            ? t("Backend.Groups.Approvals.EmptyFilter", "Nothing matches this filter.")
            : emptyText ?? t("Backend.Groups.Approvals.Empty", "No post is planned on a member's account yet. Use “Publish on the account of” in the plan dialog.")}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="data-table table-striped">
            <thead>
              <tr>
                <th>{t("Backend.Post.Title", "Post")}</th>
                {showGroup && <th>{t("Backend.Groups.Approvals.Group", "Group")}</th>}
                <th>{t("Backend.Groups.Approvals.PublishOn", "Publishes on")}</th>
                <th>{t("Backend.Groups.Approvals.PlannedBy", "Planned by")}</th>
                <th>{t("Backend.Post.Schedule.Date", "Date")}</th>
                <th>{t("Backend.Post.Schedule.Platform", "Platform")}</th>
                <th>{t("Backend.Groups.Approvals.Status", "Approval")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openPreview(s)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPreview(s); } }}
                  title={needsMe(s)
                    ? t("Backend.Groups.Approvals.NeedsYou", "Waiting for your approval – click to decide")
                    : t("Backend.Groups.Approvals.ShowPost", "Show the full post")}
                  style={needsMe(s)
                    ? { cursor: "pointer", background: "rgba(247,204,83,0.14)", boxShadow: "inset 4px 0 0 #e0a800" }
                    : { cursor: "pointer" }}
                >
                  <td style={{ fontWeight: needsMe(s) ? 700 : 500, color: "#1a2236", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {needsMe(s) && <FontAwesomeIcon icon={faHourglassHalf} style={{ color: "#8a5a00", marginRight: 8 }} />}
                    {s.post?.title || t("Backend.Approvals.Untitled", "Untitled post")}
                  </td>
                  {showGroup && (
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        {groupsOf(s).map((g) => (
                          <span key={g.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#1a2236" }}>
                            <GroupLogo groupId={g.id} size={20} title={g.name} fallback={groupInitials(g.name)}
                              style={{ borderRadius: 6, background: "rgba(86,74,177,0.12)", color: "#564ab1", fontSize: 9, fontWeight: 800 }} />
                            {g.name}
                          </span>
                        ))}
                        {groupsOf(s).length === 0 && <span style={{ color: "#9299a8" }}>—</span>}
                      </span>
                    </td>
                  )}
                  <td>{personName(s.publish_as) || "- -"}</td>
                  <td style={{ fontSize: 12, color: "#74788d" }}>{personName(s.planned_by) || "- -"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{formatDate(s.local_user_time)} <span style={{ fontSize: 11, color: "#74788d" }}>{s.timezone}</span></td>
                  <td style={{ textTransform: "capitalize", fontSize: 12 }}>{(s.platforms ?? ["linkedin"]).join(", ")}</td>
                  <td>
                    <ApprovalBadge status={s.approval_status} title={s.approval_comment} />
                    {s.approval_comment && s.approval_status !== "approved" && (
                      <div style={{ fontSize: 11, color: "#74788d", marginTop: 4, maxWidth: 260 }}>{s.approval_comment}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default ApprovalScheduleList;
